import {
  CONNECTOR_VALUE_SCHEMAS,
  type ConnectorConfig,
  type ConnectorRegistryEntry,
  type ConnectorValueDataType
} from '@kioskforge/shared';

/**
 * X3.3/X3.4/X3.5 — the shared data layer for the connector-backed widgets
 * (weather, news, calendar). Pure: no React, no network. The widgets render
 * *normalised* values only, never a provider payload (D0: swapping providers
 * changes the registry and the connector plugin, never widget code).
 *
 * The binding is deliberately a plain object rather than a hook: the player
 * (f10) hydrates it from `data_points`, and the preview/golden path hydrates it
 * from `WIDGET_SAMPLE`, so both render the same tree from the same code.
 *
 * State selection follows docs/DATA_WIDGET_SPEC.md §4 (and CONNECTORS.md §4 for
 * the stale rule): absent value + no configured connector = not-configured;
 * absent value + a configured connector = pending; unparseable or
 * `fetch_state: 'error'` = error; `fetch_state: 'stale'` = render + a stale
 * indicator; otherwise ready.
 */

/** The frozen instant the sample values are taken at — the clock's instant, so
 *  the preview and A's goldens agree across widgets (WIDGET_SVG_SPECS.md). */
export const WIDGET_SAMPLE_ISO = '2024-01-01T09:41:07Z';

/** The default display zone. Every rendered time takes a zone explicitly: the
 *  ambient zone is never read, which is what makes the DST test deterministic. */
export const DEFAULT_TIMEZONE = 'Europe/London';

export type WidgetDataBinding = {
  /** Normalised values keyed by `data_key` (mirrors `data_points`). */
  values: Record<string, unknown>;
  /** The tenant's connector configs (Integrations screen, X3.6). */
  configs: ConnectorConfig[];
  /** The operator registry: one entry per provider (CONNECTORS.md §1). */
  registry: ConnectorRegistryEntry[];
  /** IANA zone for every displayed time. Defaults to `DEFAULT_TIMEZONE`. */
  timezone?: string;
  /** Instant the values were produced from, for goldens/preview. */
  now?: string;
};

/**
 * The outcome of binding a `data_key` to a schema-validated value. `ready` and
 * `stale` carry the value; the other three are terminal display states so a
 * widget never renders a blank region (a blank region on a kiosk reads broken).
 */
export type WidgetDataState<T> =
  | { status: 'ready'; value: T }
  | { status: 'stale'; value: T; lastGood: string }
  | { status: 'not-configured' }
  | { status: 'pending' }
  | { status: 'error'; reason: string };

/**
 * The registry entry the tenant is actually using for a data type, or null.
 * Deterministic: filtered to `enabled && configured` configs that resolve to an
 * enabled registry entry of the right type, then sorted by `connector_id` so the
 * choice does not depend on config insertion order.
 */
export function selectProvider(
  binding: WidgetDataBinding,
  dataType: ConnectorValueDataType
): ConnectorRegistryEntry | null {
  const matches: ConnectorRegistryEntry[] = [];
  for (const config of binding.configs) {
    if (!config.enabled || !config.configured) continue;
    const entry = binding.registry.find((candidate) => candidate.id === config.connector_id);
    if (entry && entry.enabled && entry.data_type === dataType) matches.push(entry);
  }
  matches.sort((a, b) => a.id.localeCompare(b.id));
  return matches[0] ?? null;
}

/** True when the tenant has a usable (enabled + configured) connector for the type. */
export function hasConnector(binding: WidgetDataBinding, dataType: ConnectorValueDataType): boolean {
  return selectProvider(binding, dataType) !== null;
}

/**
 * Bind a `data_key` to a value, validated against the connector's value schema.
 * A malformed value is an `error` (never rendered as if it were good data).
 */
export function loadValue<T>(
  binding: WidgetDataBinding,
  dataType: ConnectorValueDataType,
  dataKey: string
): WidgetDataState<T> {
  const raw = binding.values[dataKey];
  if (raw === undefined || raw === null) {
    return hasConnector(binding, dataType) ? { status: 'pending' } : { status: 'not-configured' };
  }

  const parsed = CONNECTOR_VALUE_SCHEMAS[dataType].safeParse(raw);
  if (!parsed.success) return { status: 'error', reason: `${dataType} value failed validation` };

  const value = parsed.data as T;
  const fetchState = (value as { fetch_state?: string }).fetch_state;
  if (fetchState === 'error') return { status: 'error', reason: 'upstream fetch failed' };
  if (fetchState === 'stale') {
    return { status: 'stale', value, lastGood: (value as { fetched_at: string }).fetched_at };
  }
  return { status: 'ready', value };
}

export type WidgetAttribution = { required: boolean; text: string; url?: string };

/**
 * The attribution the licence requires, or null. The value's own attribution
 * wins (it travels with the data); otherwise the selected registry entry's
 * declaration applies. Attribution text lives in the registry, never in widget
 * code (D0), and is only rendered when `required` is true.
 */
export function attributionFor(
  value: { attribution?: WidgetAttribution },
  binding: WidgetDataBinding,
  dataType: ConnectorValueDataType
): WidgetAttribution | null {
  const fromValue = value.attribution;
  if (fromValue && fromValue.required) return fromValue;
  const entry = selectProvider(binding, dataType);
  if (entry && entry.attribution.required) {
    return { required: true, text: entry.attribution.text, url: entry.attribution.url };
  }
  return null;
}

/**
 * The attribution line to render, or null. A required attribution with no text
 * is a defect, not a silent omission: it renders a marker so the gap is visible.
 */
export function attributionText(
  value: { attribution?: WidgetAttribution },
  binding: WidgetDataBinding,
  dataType: ConnectorValueDataType
): string | null {
  const attribution = attributionFor(value, binding, dataType);
  if (!attribution) return null;
  return attribution.text.trim().length > 0 ? attribution.text : 'ATTRIBUTION REQUIRED';
}

/** True when a value was served past its refresh interval (CONNECTORS.md §4). */
export function isStale(value: { fetch_state?: string }): boolean {
  return value.fetch_state === 'stale';
}

/**
 * A temperature for display. `units` is the *display* preference (the value's
 * `units` is the stored form and `temp_c` is always Celsius). Metric shows a
 * bare degree, imperial an explicit °F — never a silent unit switch.
 */
export function formatTemp(tempC: number, units: 'metric' | 'imperial'): string {
  if (units === 'imperial') return `${Math.round((tempC * 9) / 5 + 32)}°F`;
  return `${Math.round(tempC)}°`;
}

/** A UTC instant as a wall-clock time in an explicit zone (never the ambient one). */
export function formatLocalTime(iso: string, timezone: string = DEFAULT_TIMEZONE): string {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(at);
}

/** A UTC instant as `Mon 1 Jan` in an explicit zone. */
export function formatLocalDay(iso: string, timezone: string = DEFAULT_TIMEZONE): string {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  }).format(at);
}

/** The calendar day (in `timezone`) a UTC instant falls on, as `YYYY-MM-DD`. */
export function localDayKey(iso: string, timezone: string = DEFAULT_TIMEZONE): string {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(at);
}

/**
 * A weekday label for a `YYYY-MM-DD` string. Parsed at UTC noon so no local
 * offset can shift the day — an ISO date has no zone and must not acquire one.
 */
export function formatDayLabel(isoDate: string): string {
  const at = Date.parse(`${isoDate}T12:00:00Z`);
  if (Number.isNaN(at)) return isoDate;
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'short' }).format(at);
}

// ---------------------------------------------------------------------------
// Sample hydration — previews and (later) A's golden tests.
// ---------------------------------------------------------------------------

function registryEntry(overrides: Partial<ConnectorRegistryEntry> & Pick<ConnectorRegistryEntry, 'id' | 'data_type' | 'provider_name' | 'licence_class'>): ConnectorRegistryEntry {
  return {
    attribution: { required: false, text: `${overrides.provider_name} data` },
    domains: ['example.com'],
    byo_supported: true,
    brokered: false,
    refresh_interval_s: 900,
    rate_budget_per_hour: 60,
    enabled: true,
    ...overrides
  };
}

/** Two weather providers behind one widget — proof the widget is provider-agnostic. */
export const WIDGET_SAMPLE_REGISTRY: ConnectorRegistryEntry[] = [
  registryEntry({
    id: 'open-meteo',
    data_type: 'weather',
    provider_name: 'Open-Meteo',
    licence_class: 'byo_key_only',
    attribution: { required: true, text: 'Weather data by Open-Meteo', url: 'https://open-meteo.com/' }
  }),
  registryEntry({
    id: 'openweathermap',
    data_type: 'weather',
    provider_name: 'OpenWeatherMap',
    licence_class: 'byo_key_only',
    attribution: { required: false, text: 'OpenWeatherMap' }
  }),
  registryEntry({
    id: 'tenant-rss',
    data_type: 'news',
    provider_name: 'Tenant RSS',
    licence_class: 'commercial_ok'
  }),
  registryEntry({
    id: 'tenant-ics',
    data_type: 'calendar',
    provider_name: 'Tenant ICS',
    licence_class: 'commercial_ok'
  })
];

export const WIDGET_SAMPLE_CONFIGS: ConnectorConfig[] = [
  { tenant: 'sample', connector_id: 'open-meteo', path: 'byo', params: { lat: 51.45, lon: -2.59 }, configured: true, enabled: true },
  { tenant: 'sample', connector_id: 'tenant-rss', path: 'byo', params: { feed: 'https://example.com/feed.xml' }, configured: true, enabled: true },
  { tenant: 'sample', connector_id: 'tenant-ics', path: 'byo', params: { feed: 'https://example.com/room.ics' }, configured: true, enabled: true }
];

export const WIDGET_SAMPLE_VALUES = {
  'weather.home': {
    fetch_state: 'fresh' as const,
    fetched_at: WIDGET_SAMPLE_ISO,
    units: 'metric' as const,
    location_label: 'Bristol, UK',
    now: { temp_c: 7.4, feels_like_c: 5.1, condition: 'Cloudy', icon: 'cloud', wind_kph: 14, humidity: 82, uv: 1, rain_chance: 20 },
    hourly: [
      { at: '2024-01-01T10:00:00Z', temp_c: 8.1, condition: 'Cloudy', rain_chance: 15 },
      { at: '2024-01-01T11:00:00Z', temp_c: 8.6, condition: 'Cloudy', rain_chance: 10 },
      { at: '2024-01-01T12:00:00Z', temp_c: 9.2, condition: 'Sunny', rain_chance: 5 },
      { at: '2024-01-01T13:00:00Z', temp_c: 9.4, condition: 'Sunny', rain_chance: 5 }
    ],
    daily: [
      { date: '2024-01-01', min_c: 4, max_c: 9, condition: 'Cloudy', rain_chance: 20, uv: 1 },
      { date: '2024-01-02', min_c: 5, max_c: 10, condition: 'Rain', rain_chance: 70, uv: 0 },
      { date: '2024-01-03', min_c: 3, max_c: 8, condition: 'Sunny', rain_chance: 5, uv: 1 },
      { date: '2024-01-04', min_c: 2, max_c: 7, condition: 'Cloudy', rain_chance: 30, uv: 1 },
      { date: '2024-01-05', min_c: 4, max_c: 9, condition: 'Cloudy', rain_chance: 25, uv: 1 }
    ]
  },
  'news.local': {
    fetch_state: 'fresh' as const,
    fetched_at: WIDGET_SAMPLE_ISO,
    items: [
      { title: 'City bridge reopens after repairs', source: 'Bristol Post', url: 'https://example.com/newbridge', published_at: '2024-01-01T08:10:00Z' },
      { title: 'Harbour festival returns in May', source: 'BBC Bristol', url: 'https://example.com/festival', published_at: '2024-01-01T07:02:00Z' },
      { title: 'New cycle lane approved', source: 'Bristol Post', url: 'https://example.com/cycle' }
    ]
  },
  'calendar.room': {
    fetch_state: 'fresh' as const,
    fetched_at: WIDGET_SAMPLE_ISO,
    events: [
      { title: 'Team stand-up', start: '2024-01-01T09:30:00Z', end: '2024-01-01T09:45:00Z', location: 'Room 2', all_day: false },
      { title: 'Design review', start: '2024-01-01T11:00:00Z', end: '2024-01-01T12:00:00Z', location: 'Room 1', all_day: false },
      { title: 'Fire drill', start: '2024-01-01T14:30:00Z', end: '2024-01-01T14:45:00Z', all_day: false },
      { title: 'Public holiday', start: '2024-01-02T00:00:00Z', all_day: true }
    ]
  }
};

/**
 * A binding with no values and no connectors — the correct default for a widget
 * rendered without hydration (a bare preview or a test). Every data widget then
 * shows its not-configured state rather than guessing.
 */
export const EMPTY_BINDING: WidgetDataBinding = { values: {}, configs: [], registry: [] };

/** A ready-made binding for previews and goldens (see WIDGET_SVG_SPECS.md). */
export const WIDGET_SAMPLE: WidgetDataBinding = {
  now: WIDGET_SAMPLE_ISO,
  timezone: DEFAULT_TIMEZONE,
  registry: WIDGET_SAMPLE_REGISTRY,
  configs: WIDGET_SAMPLE_CONFIGS,
  values: WIDGET_SAMPLE_VALUES
};
